// --- Dependencies -------------------------------------------------------------------------------
import { useMemo } from "react";
import { NavLink } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyOverviewMeta, TyOverviewStores, TyStoreType } from "@/types";
import Chart from "@/UI/atoms/Chart";
import StatTile from "@/UI/atoms/StatTile";
import { alignToBuckets, buildBuckets, formatBucket, formatNumber, rangeLabel } from "./helpers";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    data: TyOverviewStores;
    meta: TyOverviewMeta;
};

// --- Logic --------------------------------------------------------------------------------------

export default function StoresSection({ data, meta }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);

    const period = rangeLabel(meta.range, t);

    const growth = useMemo(() => {
        const buckets = buildBuckets(meta.from, meta.to, meta.unit, data.series[0]?.at);
        return {
            labels: buckets.map((slot) => formatBucket(slot, meta.unit, lang)),
            values: alignToBuckets(data.series, buckets, meta.unit, (row) => row.value),
        };
    }, [data.series, meta.from, meta.to, meta.unit, lang]);

    const count = (value: number) => formatNumber(value, lang);

    return (
        <section className="home-section">
            <h2 className="home-section-title">{t.home.storesTitle}</h2>

            <div className="home-tiles">
                <StatTile label={t.home.storesTotal} value={count(data.total)} tone="brand" to="/stores" />
                <StatTile label={t.home.storesNew} value={count(data.created)} hint={period} tone="good" to="/stores" />
                <StatTile
                    label={t.home.storesSubscribed}
                    value={count(data.subscribed)}
                    hint={t.home.storesSubscribedHint}
                    tone="good"
                    to="/subscriptions"
                />
                {/* Log-backed, so its window is the retention one, not the selected one. */}
                <StatTile label={t.home.storesActive} value={count(data.active)} hint={t.home.activityHint} to="/logs" />
                <StatTile
                    label={t.home.storesPaused}
                    value={count(data.paused)}
                    hint={t.home.storesSubscribedHint}
                    tone={data.paused > 0 ? "bad" : "neutral"}
                    to="/stores"
                />
                <StatTile label={t.home.storesNeverPaid} value={count(data.neverPaid)} tone={data.neverPaid > 0 ? "warn" : "neutral"} to="/stores" />
            </div>

            <div className="home-cards home-cards--triple">
                <div className="home-card">
                    <p className="home-card-title">{t.home.storesGrowth}</p>
                    <Chart
                        kind="bar"
                        caption={t.home.storesGrowth}
                        labels={growth.labels}
                        series={[{ label: t.home.storesNew, data: growth.values, color: "brand" }]}
                        emptyLabel={t.home.noData}
                    />
                </div>

                <div className="home-card">
                    <p className="home-card-title">{t.home.storesByType}</p>
                    <Chart
                        kind="doughnut"
                        caption={t.home.storesByType}
                        labels={data.byType.map((row) => t.enums.storeType[row.key as TyStoreType] ?? row.key)}
                        series={[{ label: t.home.storesByType, data: data.byType.map((row) => row.value) }]}
                        emptyLabel={t.home.noData}
                    />
                </div>

                <div className="home-card">
                    <p className="home-card-title">{t.home.storesTopActive}</p>
                    {data.topActive.length === 0 ? (
                        <p className="home-empty">{t.home.noData}</p>
                    ) : (
                        <ul className="home-list">
                            {data.topActive.map((store) => (
                                <li key={store._id}>
                                    <NavLink className="home-list-row" to={`/stores/${store._id}`}>
                                        <span className="home-list-avatar">
                                            {store.logo ? (
                                                <img src={store.logo} alt="" />
                                            ) : (
                                                <img className="home-list-avatar-fallback" src="/icons/store-icon.svg" alt="" />
                                            )}
                                        </span>
                                        <span className="home-list-main">
                                            <span className="home-list-name">{store.name}</span>
                                            <span className="home-list-sub" dir="ltr">
                                                {store.slug}
                                            </span>
                                        </span>
                                        <span className="home-list-value">{count(store.requests)}</span>
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    );
}
